/**
 * Henry's four result slots.
 *
 * REVERSES the rule decided 2026-08-28. That one read the four slots as a budget to be
 * filled: cap the makes at four, then backfill any spare room with near-misses, so two
 * makes rendered alongside two near-misses. Henry looked at that on 2026-09-01 and said
 * the opposite:
 *
 *   "look for 4 makes. If even 1 make no need for any near misses. If zero makes then
 *   post 4 near misses. Never post the No's."
 *   "if there is 1 solution that works, the near misses are pointless."
 *
 * The second sentence is the reason, and it is not about screen space. A near-miss is a
 * piece of homework - restructure the file, drop the loan, wait out the seasoning - and
 * homework is only worth printing when there is no lender who will fund the deal as it
 * stands today. Put one working lender next to it and the broker is being asked to
 * choose between money now and money after rework, which is not a choice anybody makes.
 * So the two lists are exclusive, not complementary: makes OR near-misses, never a mix.
 *
 * Declines are gone from both paths. "Never post the No's" is flat, and it settles a
 * question the old rule got wrong in the other direction, where every decline rode along
 * under the makes as evidence. Henry does not want the broker reading a list of banks
 * that said no; he wants the banks that said yes, or - failing that - the shortest route
 * to a yes. The evidence is still there for anyone who needs it, because this trims what
 * RENDERS and nothing else.
 *
 * ONE SLOT PER LENDER, from the same message:
 *
 *   "3 JLD programs should not take up 3 slots. 1 slot per bank."
 *
 * A slot is a bank, not a program. Findings arrive per lender per delivery channel, so a
 * lender with three qualifying programs produces three findings and, under the old rule,
 * three quarters of the broker's results were one phone call. Collapse to the lender's
 * best program and the four slots are four banks - four different calls, four different
 * chances - which is what the broker is actually shopping.
 *
 * PRESENTATION ONLY, and that boundary is load-bearing. L2 still sweeps every lender and
 * synthesis still writes a finding for each one it has an answer for, because a zero
 * result has to stay a proof - "40 evaluated, 0 eligible" is a different claim from "we
 * stopped looking at four". This trims what renders; nothing upstream sees the cap, and
 * the eval suite scores the full answer.
 *
 * Note what left with the old rule: near-misses used to be sorted by whether their fix
 * contained a number, on the theory that an actionable fix should win the last spare
 * slot from an unfixable one. There is no spare slot any more - near-misses either own
 * all four or none of them - and Henry has since given an explicit order for the four,
 * which is the house stack rank. A heuristic does not get to outrank an instruction.
 */

/** The verdicts that mean a lender can actually do the loan. */
const MAKES = new Set(['yes', 'yes_with_conditions']);

/**
 * What both lists need to be slotted: who the lender is, and where the house ranks it.
 *
 * Rank is supplied by the caller, never looked up here. It lives on the lender row
 * (migration 019, 1 first and 100 for unranked) and it is a commercial preference, not
 * an eligibility fact - which is exactly why it may only be applied at this end, to a
 * finished verdict set, where it can decide reading order without touching an answer.
 */
export interface SlotCandidate {
  readonly lenderId: string;
  /** House reading order, 1 first. See migration 019; 100 is the unranked default. */
  readonly stackRank: number;
  /**
   * Max LTV this program allows, when the caller has it. Used only to pick which of one
   * lender's programs gets that lender's single slot; it never reorders lenders.
   */
  readonly maxLtv?: number | null;
}

export interface SlotFinding extends SlotCandidate {
  readonly verdict: string;
}

export type SlotNearMiss = SlotCandidate;

export const RESULT_SLOTS = 4;

/**
 * One card per lender: the lender's best program, and only that one.
 *
 * "Best" is the highest max LTV, because among programs that all say yes the one with
 * the most room is the one the broker can size up into, and it is the only ordering
 * signal that survives on the finding itself. A program that reports a max LTV beats one
 * that reports none - a known ceiling is worth more to a broker than an unknown one -
 * and when nobody in the group reports one, the first wins, which keeps synthesis's own
 * ordering intact rather than inventing a new one.
 *
 * Equal max LTVs also keep input order, so the same answer renders the same card twice
 * in a row. Nothing here mutates the group.
 */
function bestPerLender<T extends SlotCandidate>(candidates: readonly T[]): T[] {
  const byLender = new Map<string, T>();
  for (const c of candidates) {
    const held = byLender.get(c.lenderId);
    if (!held) {
      byLender.set(c.lenderId, c);
      continue;
    }
    const mine = typeof c.maxLtv === 'number' ? c.maxLtv : null;
    const theirs = typeof held.maxLtv === 'number' ? held.maxLtv : null;
    if (mine !== null && (theirs === null || mine > theirs)) byLender.set(c.lenderId, c);
  }
  return [...byLender.values()];
}

/**
 * Rank ascending, ties left exactly as they arrived.
 *
 * Array#sort is stable, so two lenders at the unranked default of 100 render in the
 * order synthesis wrote them instead of drifting between runs - which matters because
 * most of the roster is unranked and would otherwise be a coin flip on every reload.
 */
function byStackRank<T extends SlotCandidate>(candidates: readonly T[]): T[] {
  return [...candidates].sort((a, b) => a.stackRank - b.stackRank);
}

/**
 * Trim an answer to the four slots: makes if there are any, near-misses if there are not.
 *
 * Returns new arrays; the input is not mutated, so a caller can still reach the full
 * answer for a "show everything" affordance or for logging.
 */
export function applyResultSlots<F extends SlotFinding, N extends SlotNearMiss>(
  findings: readonly F[],
  nearMisses: readonly N[],
  slots: number = RESULT_SLOTS,
): { findings: F[]; nearMisses: N[] } {
  const makes = byStackRank(bestPerLender(findings.filter((f) => MAKES.has(f.verdict))));

  // One make is enough to make every near-miss pointless, so this is a hard fork rather
  // than a fill: the near-miss list is not shortened here, it is not printed at all.
  if (makes.length > 0) return { findings: makes.slice(0, slots), nearMisses: [] };

  // Zero makes. The near-misses are now the whole answer and get all four slots, under
  // the same one-per-lender rule - four banks to work on, not four programs at one bank.
  return {
    findings: [],
    nearMisses: byStackRank(bestPerLender(nearMisses)).slice(0, slots),
  };
}

/* ============================================================ ONE ANSWER, ONE ORDER
 * Everything below exists so the results page and Henry's email cannot disagree.
 *
 * They did. The page called `applyResultSlots`; the email carried a hand-written second
 * copy of the same product rule, added deliberately while this function was mid-rewrite
 * and left in place after it landed. The two copies then diverged on the one input the
 * email did not have: the page decorates each finding with the lender's `stackRank` off
 * the verdict roster and sorts by it, and the email could not, so it fell back to the
 * order synthesis happened to return.
 *
 * With four slots and twenty findings those are simply different lists. Henry, 2026-09-02:
 * "the email had 4 completely diff lenders than the UI... how did that happen lol".
 *
 * That is what a duplicated rule costs, and the fix is not a third copy. The decoration
 * moved here with the rule, so a caller supplies the raw answer plus the verdict roster
 * and gets back what to show. There is now nowhere left to disagree.
 */

/** A verdict row, reduced to the fields the slot rule and its decoration read. */
export interface SlotChannel {
  readonly lenderId: string;
  readonly stackRank: number;
  readonly maxLtv: number | null;
  readonly investorSlug: string;
  readonly investorName: string;
  readonly isBaseChannel: boolean;
}

/** The unranked default, matching migration 019's column default. */
export const UNRANKED_STACK_RANK = 100;

const norm = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/** Words the base/retained descriptor is built out of, in any of its observed spellings. */
// "standard" since 2026-09-15, when the descriptor stopped saying "base / retained".
const BASE_WORDS = /\b(base|retained|retain|portfolio|standard)\b/;

/**
 * The grid rows a finding's channel sentence recognises.
 *
 * A finding's only handle on its channel is `deliveryChannel`, a free STRING the model
 * wrote after reading a rendered descriptor - "base / retained", "delivered to TransAm",
 * and the bare "TransAm" has been observed. It is not a slug and the answer schema does
 * not validate it, so there is no key to join on; there is only a sentence to recognise.
 *
 * Recognition is conservative and returns a LIST. A lender with three programs on one
 * investor produces three rows that a single sentence matches equally well, and picking
 * one would be inventing a fact. Callers take the maximum and read an empty list as
 * "unknown", which is what it is.
 */
export function channelsFor<C extends SlotChannel>(
  channels: readonly C[], deliveryChannel: string,
): C[] {
  const text = norm(deliveryChannel);
  if (!text) return [];
  return channels.filter((c) => {
    if (c.isBaseChannel) return BASE_WORDS.test(text);
    // Both spellings, because the model copies a rendered name but has been seen to
    // shorten it. Empty strings are excluded first: `''.includes` is vacuously true and
    // would match every finding to every channel of a lender with an unnamed investor.
    const name = norm(c.investorName);
    const slug = norm(c.investorSlug);
    return (name.length > 0 && text.includes(name))
        || (slug.length > 0 && text.includes(slug));
  });
}

/**
 * What to show, from the raw answer and the verdict roster.
 *
 * `hiddenMakes` is the count of lenders that fit and did not get a slot. It counts
 * LENDERS, not findings, because that is what the slots hold - a lender with three
 * programs that all fit took one slot and hides nothing.
 */
export function slotAnswer<F extends { lenderId: string; verdict: string;
                                       deliveryChannel?: string },
                           N extends { lenderId: string }>(
  findings: readonly F[],
  nearMisses: readonly N[],
  channels: readonly SlotChannel[],
  slots: number = RESULT_SLOTS,
): { findings: F[]; nearMisses: N[]; hiddenMakes: number } {
  const rankById = new Map<string, number>();
  const byLender = new Map<string, SlotChannel[]>();
  for (const c of channels) {
    if (!rankById.has(c.lenderId)) rankById.set(c.lenderId, c.stackRank);
    const held = byLender.get(c.lenderId);
    if (held) held.push(c);
    else byLender.set(c.lenderId, [c]);
  }
  const rankOf = (id: string) => rankById.get(id) ?? UNRANKED_STACK_RANK;

  const decorated = findings.map((f) => {
    const rows = channelsFor(byLender.get(f.lenderId) ?? [], f.deliveryChannel ?? '');
    const ltvs = rows.map((c) => c.maxLtv).filter((n): n is number => n !== null);
    // null, not undefined: `exactOptionalPropertyTypes` refuses an explicit undefined on
    // an optional field, and the rule reads null as "no ceiling reported".
    return { ...f, stackRank: rankOf(f.lenderId), maxLtv: ltvs.length > 0 ? Math.max(...ltvs) : null };
  });
  // Near-misses get rank and nothing else: their schema carries no channel to join on.
  const nearDecorated = nearMisses.map((n) => ({ ...n, stackRank: rankOf(n.lenderId) }));

  const shown = applyResultSlots(decorated, nearDecorated, slots);
  const lendersThatFit = new Set(
    decorated.filter((f) => MAKES.has(f.verdict)).map((f) => f.lenderId)).size;

  return {
    findings: shown.findings as unknown as F[],
    nearMisses: shown.nearMisses as unknown as N[],
    hiddenMakes: Math.max(0, lendersThatFit - shown.findings.length),
  };
}
